import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/solid";
import { cn } from "@moe/priv/utils";
import { formatCount } from "@web/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import { useState } from "react";

const variant = cva("transition duration-300 hover:scale-125", {
  variants: {
    color: {
      primary: "fill-white",
      secondary: "fill-tx-tertiary"
    },
    size: {
      sm: "size-5",
      md: "size-6"
    }
  },
  defaultVariants: {
    color: "primary",
    size: "md"
  }
});

const textSizeVariant = cva("font-semibold text-neutral-100", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm"
    },
    color: {
      primary: "text-white",
      secondary: "text-tx-tertiary"
    }
  },
  defaultVariants: {
    color: "primary",
    size: "md"
  }
});

const layoutVariant = cva("flex items-center justify-center", {
  variants: {
    layout: {
      horizontal: "flex-row space-x-1",
      vertical: "flex-col space-y-1"
    }
  },
  defaultVariants: {
    layout: "vertical"
  }
});

interface Props extends VariantProps<typeof variant>, VariantProps<typeof layoutVariant> {
  messageCount: number;
  onClick: (e: React.SyntheticEvent) => void;
  className?: string;
}

export function ChatButton({ layout, messageCount, onClick, className, color, size }: Props) {
  const [isAnimating, setIsAnimating] = useState(false);

  return (
    <button
      className={cn(layoutVariant({ layout }))}
      onClick={(e) => {
        setIsAnimating(true);
        onClick(e);
      }}
      onPointerDownCapture={(e) => e.stopPropagation()}
    >
      <motion.div
        animate={isAnimating ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.15 }}
        onAnimationComplete={() => setIsAnimating(false)}
      >
        <ChatBubbleOvalLeftIcon className={cn(variant({ color, size }), className)} />
      </motion.div>
      <span className={cn(textSizeVariant({ size, color }))}>{formatCount(messageCount)}</span>
    </button>
  );
}
